import { Link, Navigate, useLocation } from 'react-router-dom';
import { BarChart3, ExternalLink, Store } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/shared/lib/auth';
import { decimal } from '@/shared/lib/format';
import { PageHeader } from '@/shared/components/page-header';
import { MetabaseIframePanel, useUserMetabaseEmbed } from '@/shared/components/metabase-iframe-panel';
import { EmptyState } from '@/shared/components/empty-state';
import { KpiCard } from '@/shared/components/kpi-card';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  canCreateGiftcard,
  fetchOwnerDashboard,
  giftCardInviteUrl,
  parseOwnerStores,
  passbookLogoUrl,
  requestOwnerExport,
} from '../api/use-owner';

export function OwnerPage() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const showStores = pathname.includes('/lojas');
  const hasMetabase = Boolean(user?.metabase?.dashboard);

  const metabase = useUserMetabaseEmbed('owner', !showStores && hasMetabase);
  const dashboard = useQuery({
    queryKey: ['owner', user?._id],
    queryFn: () => fetchOwnerDashboard(user!._id),
    enabled: Boolean(user && (showStores || !hasMetabase)),
  });

  const [exportOpen, setExportOpen] = useState(false);
  const [exportKind, setExportKind] = useState<'data' | 'users'>('data');
  const [exportEmail, setExportEmail] = useState('');

  if (!user || user.role !== 'Owner') {
    return <Navigate to="/login" replace />;
  }

  const stores = dashboard.data ? parseOwnerStores(dashboard.data) : [];

  const handleExport = async () => {
    if (!exportEmail) return;
    try {
      await requestOwnerExport(exportKind, exportEmail);
      toast.success('Exportação solicitada. Verifique seu e-mail.');
      setExportOpen(false);
      setExportEmail('');
    } catch {
      toast.error('Falha ao solicitar exportação.');
    }
  };

  return (
    <>
      <PageHeader
        title={showStores ? 'Minhas lojas' : 'Visão geral'}
        description={showStores ? 'Cartões e lojas da franquia.' : 'Dashboard consolidado do owner.'}
        actions={
          showStores ? (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setExportKind('data');
                  setExportOpen(true);
                }}
              >
                Exportar dados
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setExportKind('users');
                  setExportOpen(true);
                }}
              >
                Exportar leads
              </Button>
            </div>
          ) : hasMetabase ? (
            <Button variant="secondary" asChild>
              <Link to="/owner/lojas">Ver lojas</Link>
            </Button>
          ) : null
        }
      />

      {!showStores && hasMetabase ? (
        <MetabaseIframePanel
          src={metabase.data?.iframeUrl}
          title="Dashboard Owner"
          isLoading={metabase.isLoading}
          isError={metabase.isError}
          errorMessage={metabase.error instanceof Error ? metabase.error.message : undefined}
          icon={BarChart3}
        />
      ) : (
        <>
          {dashboard.isLoading ? (
            <div className="mb-6 grid gap-4 sm:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-28 rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="mb-8 grid gap-4 sm:grid-cols-3">
              <KpiCard label="Total clientes" value={decimal(dashboard.data?.total ?? stores.length)} icon={Store} />
              <KpiCard label="Lojas" value={decimal(stores.length)} icon={Store} />
              <KpiCard label="Resgates" value={decimal(Number(dashboard.data?.resgates ?? 0))} icon={BarChart3} />
            </div>
          )}

          {dashboard.isError ? (
            <EmptyState icon={Store} title="Erro ao carregar lojas" description="Tente novamente mais tarde." />
          ) : dashboard.isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-72 rounded-2xl" />
              ))}
            </div>
          ) : stores.length === 0 ? (
            <EmptyState icon={Store} title="Nenhuma loja" description="Suas lojas aparecerão aqui." />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {stores.map((store) => (
                <Card key={store.passbookId} className="overflow-hidden">
                  <div
                    className="flex h-36 items-center justify-center p-4"
                    style={{ backgroundColor: store.backgroundColor ?? 'hsl(var(--muted))' }}
                  >
                    <img src={passbookLogoUrl(store.passbookId)} alt={store.clientName} className="max-h-full max-w-full object-contain" />
                  </div>
                  <div className="space-y-3 p-5">
                    <h3 className="text-lg font-semibold">{store.clientName}</h3>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" asChild>
                        <Link to={`/loja/${store.passbookId}`}>
                          <ExternalLink className="size-4" /> Inspecionar
                        </Link>
                      </Button>
                      {canCreateGiftcard(store, user._id) ? (
                        <Button size="sm" variant="success" asChild>
                          <a
                            href={giftCardInviteUrl(store.passbookId)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <ExternalLink className="size-4" /> Criar Cartão
                          </a>
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{exportKind === 'users' ? 'Exportar leads' : 'Exportar dados'}</DialogTitle>
          </DialogHeader>
          <Input
            type="email"
            placeholder="E-mail para receber o arquivo"
            value={exportEmail}
            onChange={(e) => setExportEmail(e.target.value)}
          />
          <DialogFooter>
            <Button variant="secondary" onClick={() => setExportOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleExport}>Enviar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
